import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, Snackbar, Alert, Card, CardContent, Grid, Divider, Chip, Tabs, Tab } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import { useNavigate } from 'react-router-dom';
import { useCart } from './CartContext';
import data from './productsData.json';

const productsData = data.products;

export default function Products() {
  const { addToCart, cartItems } = useCart();
  const navigate = useNavigate();

  // Initialize quantities for all products
  const initialQuantities = {};
  productsData.forEach(p => initialQuantities[p.id] = 1);
  const [quantities, setQuantities] = useState(initialQuantities);

  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Category state
  const [selectedCategory, setSelectedCategory] = useState('all');

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const filteredProducts = selectedCategory === 'all'
    ? productsData
    : productsData.filter(p => p.category === selectedCategory);

  const getPrice = (product, quantity) => {
    if (product.type === 'box') {
      return product.pricePerPiece * product.piecesPerBox;
    }
    if (product.type === 'informative') {
      return 0; // Not purchasable
    }
    // Standard logic
    let range = product.priceRanges.find(r => quantity >= r.min && quantity <= r.max);
    if (!range) {
      const maxRange = product.priceRanges.reduce((prev, current) => (prev.max > current.max) ? prev : current);
      if (quantity > maxRange.max) {
        range = maxRange;
      } else {
        range = product.priceRanges[0];
      }
    }
    return range ? range.price : product.priceRanges[0].price;
  };

  const handleChange = (id, val) => {
    const newQty = Math.max(1, Number(val));
    setQuantities(prev => ({ ...prev, [id]: newQty }));
  };

  const handleIncrement = (id) => handleChange(id, Number(quantities[id] || 1) + 1);
  const handleDecrement = (id) => handleChange(id, Math.max(1, Number(quantities[id] || 1) - 1));

  const orderProduct = (product) => {
    const qtyToAdd = Number(quantities[product.id]);

    // Check if item is already in cart
    const existingItem = cartItems.find(item => item.id === product.id);
    const currentCartQty = existingItem ? existingItem.quantity : 0;
    const newTotalQty = currentCartQty + qtyToAdd;

    // Calculate price
    const price = getPrice(product, newTotalQty);

    addToCart({ ...product, quantity: qtyToAdd, price });

    setNotificationMessage(`${product.name} (x${qtyToAdd}) je dodata u korpu!`);
    setOpenNotification(true);
  };

  return (
    <Box sx={{
      py: 8,
      px: 2,
      background: 'linear-gradient(180deg, #f8f9fa 0%, #e8eef5 100%)',
      minHeight: { xs: "100vh", md: "40vh" },
    }}>
      <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            fontWeight: 800,
            mb: 2,
            background: 'linear-gradient(135deg, #0f2352 0%, #1a3a82 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'fadeIn 0.8s ease',
          }}
        >
          Naša Ponuda
        </Typography>

        {/* Category Filter */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#0f2352',
                height: 3,
                borderRadius: 1.5,
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                color: 'text.secondary',
                mx: 1,
                '&.Mui-selected': {
                  color: '#0f2352',
                },
              },
            }}
          >
            <Tab label="Svi proizvodi" value="all" />
            <Tab label="Ručni streč" value="rucni" />
            <Tab label="Baby Rolls" value="baby" />
            <Tab label="Mašinski streč" value="masinski" />
          </Tabs>
        </Box>

        <Grid container spacing={3} justifyContent="center">
          {filteredProducts.map((p, index) => {
            const qty = quantities[p.id] || 1;
            const inCartItem = cartItems.find(item => item.id === p.id);
            const inCartQty = inCartItem ? inCartItem.quantity : 0;

            // Determine if purchasable
            const isInformative = p.type === 'informative';
            const isBox = p.type === 'box';

            // Calculate display price
            let displayPrice = 0;
            if (isBox) {
              displayPrice = p.pricePerPiece * p.piecesPerBox;
            } else if (!isInformative) {
              displayPrice = getPrice(p, qty);
            }

            const totalPrice = qty * displayPrice;

            return (
              <Grid
                item
                key={p.id}
                xs={12}
                sm={6}
                md={3}
                lg={3}
                sx={{
                  animation: 'slideUp 0.6s ease backwards',
                  animationDelay: `${index * 0.1}s`,
                }}
              >
                <Card
                  elevation={0}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 4,
                    overflow: 'hidden',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f5f7fa 100%)',
                    boxShadow: '0 10px 40px -10px rgba(15, 35, 82, 0.12)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: inCartQty > 0
                      ? '3px solid #4caf50'
                      : '2px solid transparent',
                    backgroundImage: inCartQty > 0
                      ? 'none'
                      : 'linear-gradient(145deg, #ffffff 0%, #f5f7fa 100%), linear-gradient(135deg, #0f2352 0%, #1a3a82 50%, #2e56c4 100%)',
                    backgroundOrigin: 'border-box',
                    backgroundClip: inCartQty > 0 ? 'border-box' : 'padding-box, border-box',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 60px -10px rgba(15, 35, 82, 0.25)',
                      border: inCartQty > 0
                        ? '3px solid #66bb6a'
                        : '2px solid transparent',
                      backgroundImage: inCartQty > 0
                        ? 'none'
                        : 'linear-gradient(145deg, #ffffff 0%, #f5f7fa 100%), linear-gradient(135deg, #1a3a82 0%, #2e56c4 50%, #4a7dd9 100%)',
                    },
                    height: '100%',
                    position: 'relative',
                  }}
                >
                  {inCartQty > 0 && (
                    <Chip
                      icon={<CheckCircleIcon style={{ color: 'white' }} />}
                      label={`U korpi: ${inCartQty} ${isBox ? 'kut.' : 'kom.'}`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                        color: 'white',
                        fontWeight: 'bold',
                        zIndex: 2,
                        animation: 'pulse 2s ease-in-out infinite',
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                      }}
                    />
                  )}

                  {/* Image Section */}
                  <Box sx={{
                    height: { xs: 200, sm: 240 },
                    bgcolor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    borderBottom: '1px solid #eee',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={p.img}
                      alt={p.name}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center'
                      }}
                    />
                  </Box>

                  {/* Content Section */}
                  <CardContent sx={{ flex: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom color="#0f2352" sx={{ fontSize: '1.1rem', lineHeight: 1.3, minHeight: '2.6em' }}>
                      {p.name}
                    </Typography>

                    {/* Informative Type Display */}
                    {isInformative && (
                      <Box sx={{ mt: 'auto' }}>
                        {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {p.description}
                        </Typography>
                        <Typography variant="h6" color="#0f2352" fontWeight="bold">
                          {p.pricePerKg} RSD / kg<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>(+PDV)</Typography>
                        </Typography> */}

                        <Box sx={{ mt: 2, p: 1, bgcolor: '#e3f2fd', borderRadius: 1, textAlign: 'center' }}>
                          <Typography variant="body2" fontWeight="bold" color="#1565c0">
                            {p.palletDetails}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          fullWidth
                          sx={{ mt: 2 }}
                          onClick={() => navigate('/contact')}
                        >
                          Pošalji upit
                        </Button>
                      </Box>
                    )}

                    {/* Box Type Display */}
                    {isBox && (
                      <Box sx={{ mb: 2, mt: 'auto' }}>
                        <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2, mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Pakovanje: <b>{p.piecesPerBox} komada</b>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Cena po komadu: <b>{p.pricePerPiece} RSD</b>
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="body1" fontWeight="bold" color="#0f2352">
                            {displayPrice.toLocaleString()} RSD <span style={{ fontSize: '0.7em', fontWeight: 'normal' }}>/ kutija</span>
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Standard Type Display */}
                    {!isInformative && !isBox && (
                      <Box sx={{ mb: 2, mt: 'auto' }}>
                        <Box sx={{ p: 1.5, bgcolor: '#f4f7fc', borderRadius: 2, border: '1px solid #e0e8f5' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight="500">
                              Cena po komadu:
                            </Typography>
                            <Chip
                              label="1 – 9 komada"
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.72rem',
                                fontWeight: 'bold',
                                bgcolor: '#e3f2fd',
                                color: '#0f2352',
                                border: '1px solid #bbdefb'
                              }}
                            />
                          </Box>
                          <Typography variant="h6" fontWeight="bold" color="#0f2352">
                            {displayPrice.toLocaleString()} RSD <span style={{ fontSize: '0.65em', fontWeight: 'normal', color: '#666' }}>(+PDV)</span>
                          </Typography>

                          <Box sx={{ mt: 1, p: 0.8, bgcolor: '#fff3e0', borderRadius: 1.5, border: '1px solid #ffe0b2', display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <PhoneIcon sx={{ fontSize: 16, color: '#e65100' }} />
                            <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 'bold', lineHeight: 1.2 }}>
                              Za veće količine (10+ kom) pozvati
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {!isInformative && <Divider sx={{ my: 1.5 }} />}

                    {/* Controls (Only for purchasable) */}
                    {!isInformative && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: 2, bgcolor: '#fff', height: 32 }}>
                            <IconButton onClick={() => handleDecrement(p.id)} color="primary" size="small">
                              <RemoveIcon fontSize="small" sx={{ fontSize: 16 }} />
                            </IconButton>
                            <Typography sx={{ mx: 1, fontWeight: 'bold', minWidth: 16, textAlign: 'center', fontSize: '0.9rem' }}>{qty}</Typography>
                            <IconButton onClick={() => handleIncrement(p.id)} color="primary" size="small">
                              <AddIcon fontSize="small" sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body1" fontWeight="bold" color="#0f2352">
                              {totalPrice.toLocaleString()} RSD<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>(+PDV)</Typography>
                            </Typography>
                          </Box>
                        </Box>

                        <Button
                          variant="contained"
                          fullWidth
                          size="medium"
                          startIcon={<ShoppingCartIcon sx={{ fontSize: 20 }} />}
                          onClick={() => orderProduct(p)}
                          sx={{
                            background: 'linear-gradient(135deg, #0f2352 0%, #1a3a82 100%)',
                            borderRadius: 2,
                            py: 1,
                            textTransform: 'none',
                            fontSize: '0.95rem',
                            fontWeight: 'bold',
                            boxShadow: '0 4px 14px rgba(15, 35, 82, 0.3)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #1a3b80 0%, #2e56c4 100%)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 24px rgba(15, 35, 82, 0.4)',
                            },
                          }}
                        >
                          Dodaj {isBox ? 'pakovanje' : ''}
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Alert severity="info" sx={{ display: 'inline-flex', maxWidth: 600, mx: 'auto' }}>
            <b>Za veće količine (preko 30 komada)</b> ili veleprodajne upite, molimo vas da nas kontaktirate direktno za specijalnu ponudu.
          </Alert>
        </Box>

      </Box>

      <Snackbar
        open={openNotification}
        autoHideDuration={3000}
        onClose={() => setOpenNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenNotification(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
          {notificationMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
